import gsap from "gsap";
import barba from "@barba/core";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Observer } from "gsap/Observer";
import { state } from "./helpers/state";
import { openModalTracked } from "./helpers/modalTracker";
import { initUIEvents } from "./helpers/ui/uiEvents";
import { initHomeMap, destroyMap } from "./helpers/map/mapCore";
import { initProductPage } from "./helpers/product/productPage";
import { initLoader } from "./helpers/loader/loaderInit";
import { initGuitarsPage, destroyGuitarsPage } from "./helpers/guitarsPage";
import { initConsent } from "./helpers/consent";

// Register GSAP plugins globally
gsap.registerPlugin(ScrollTrigger, Observer);

initConsent();

// --- BARBA JS CONFIG ---
barba.init({
  sync: true,
  debug: true,

  views: [
    {
      namespace: "home",
      beforeEnter() {
        const loader = document.querySelector(
          ".overlay__loader",
        ) as HTMLElement | null;
        if (loader && !state.isFirstLoad) {
          gsap.set(loader, { autoAlpha: 0, zIndex: -1, display: "none" });
          const videos = loader.querySelectorAll("video");
          videos.forEach((v) => v.pause());
        }
      },
      afterEnter() {
        initHomeMap();
        initUIEvents();

        // --- MODAL RETURN LOGIC ---
        // If we returned from a product via modal, reopen the modal
        if (state.restoreModal && state.lastModalArgs) {
          // Use a small delay to ensure the DOM and Map are ready
          setTimeout(() => {
            // Reopening the modal, openModal.js should reinitialize Swiper
            if (state.lastModalArgs) {
              openModalTracked(
                state.lastModalArgs.elName,
                state.lastModalArgs.options,
              );
            }
          }, 300); // Light delay to sync with animation

          // Reset flag
          state.restoreModal = false;
        }
      },
      beforeLeave() {
        destroyMap();
      },
    },
    {
      namespace: "product",
      afterEnter() {
        initProductPage();
        initUIEvents();
      },
      beforeLeave() {
        ScrollTrigger.getAll().forEach((t) => t.kill());
        const obs = Observer.getById("page-overlay");
        if (obs) obs.kill();
      },
    },
    {
      namespace: "guitars",
      afterEnter() {
        const loader = document.querySelector(
          ".overlay__loader",
        ) as HTMLElement | null;
        if (loader) loader.style.display = "none";
        initGuitarsPage();
        initUIEvents();
      },
      beforeLeave() {
        destroyGuitarsPage();
      },
    },
  ],

  transitions: [
    // TRANSITION 1: Home -> Product (Click on .modal__image)
    // Effect: The modal becomes white, expands to full screen from right to left, then fade-in the page
    {
      name: "home-to-product",
      from: { namespace: "home" },
      to: { namespace: "product" },

      custom: ({ trigger }: any) => {
        return trigger.classList && trigger.classList.contains("modal__image");
      },

      leave(data: any) {
        // Flag to reopen the modal upon returning
        state.restoreModal = true;
        // Flag to auto-open the gallery on the product page
        state.autoOpenGallery = true;
        const done = (this as any).async();

        // Select the modal and its content
        const modalDialog = data.current.container.querySelector(
          ".overlay__modal",
        ) as HTMLElement | null;
        const modalContent = modalDialog ? modalDialog.children : [];

        const tl = gsap.timeline({
          onComplete: done,
        });

        if (modalDialog) {
          // 1. Instantly hide the modal content (texts, buttons)
          // leaving only the white box
          tl.to(modalContent, {
            opacity: 0,
            duration: 0.2,
            ease: "power1.out",
          });

          // 2. Expand the white box from right to left
          // Use fromTo to force the correct starting coordinates
          tl.fromTo(
            modalDialog,
            {
              maxWidth: "480px",
              width: "33%",
              backgroundColor: "#ffffff", // Ensure it is white
              zIndex: 9999,
            },
            {
              width: "100%", // Total expansion to the left
              maxWidth: "100%",
              duration: 0.8,
              ease: "expo.inOut",
              clearProps: "all",
            },
            "<", // Starts along with the text fade out
          );
        } else {
          // Safety fallback if the modal is not found
          tl.to(data.current.container, { opacity: 0, duration: 0.5 });
        }
      },

      enter(data: any) {
        // The new page enters in Fade In above the expanded white box
        return gsap.from(data.next.container, {
          opacity: 0,
          duration: 0.6,
          delay: 0.2, // Wait until the expansion is almost finished
          ease: "power2.out",
          zIndex: 10000, // Above everything
          clearProps: "all",
        });
      },
    },

    // TRANSITION 2: Product -> Home (Click on .back)
    // Keep the slide out to the right, which worked well and is consistent
    {
      name: "back-to-home",
      from: { namespace: "product" },
      to: { namespace: "home" },

      custom: ({ trigger }: any) => {
        return trigger.classList && trigger.classList.contains("back");
      },

      leave(data: any) {
        // The product slides away to the right, revealing the home underneath
        return gsap.to(data.current.container, {
          x: "100%",
          opacity: 1, // Keep full opacity while exiting
          duration: 0.8,
          ease: "expo.inOut",
          zIndex: 1000,
        });
      },

      enter(data: any) {
        // The Home appears underneath (static or light fade)
        return gsap.from(data.next.container, {
          opacity: 0.5,
          duration: 0.8,
          ease: "power2.out",
          clearProps: "all",
        });
      },
    },

    // TRANSITION 3: Generic fallback
    {
      name: "default-fade",
      leave(data: any) {
        return gsap.to(data.current.container, { opacity: 0, duration: 0.5 });
      },
      enter(data: any) {
        return gsap.from(data.next.container, { opacity: 0, duration: 0.5 });
      },
    },
  ],
});

// First load
window.addEventListener("DOMContentLoaded", () => {
  const container = document.querySelector(
    '[data-barba="container"]',
  ) as HTMLElement | null;
  if (!container) return;
  const ns = container.getAttribute("data-barba-namespace");

  if (ns === "home" && document.querySelector(".overlay__loader")) {
    initLoader();
    initHomeMap();
  } else if (ns === "product" || ns === "guitars") {
    const l = document.querySelector(".overlay__loader") as HTMLElement | null;
    if (l) l.style.display = "none";
    if (ns === "product") initProductPage();
    if (ns === "guitars") initGuitarsPage();
  }

  initUIEvents();
  state.isFirstLoad = false;
});
